import { FeatureFlag } from '@sonar-software/sonar-core';
import { FieldScopeHeader } from './FieldScopeHeader';
import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import React from 'react';

import {
	FieldApexClassesTab,
	FieldApexTriggersTab,
	FieldEmailAlertsTab,
	FieldEmailTemplatesTab,
	FieldFieldUpdatesTab,
	FieldFlowsTab,
	FieldFormulaFieldsTab,
	FieldMetadataTab,
	FieldPageLayoutsTab,
	FieldPicklistValuesTab,
	FieldProcessBuildersTab,
	FieldProfilesTab,
	FieldRelatedTechTab,
	FieldReportsTab,
	FieldTimelineTab,
	FieldValidationRulesTab,
	FieldWorkflowRulesTab
} from '.';
import { PeriscopeBaseComponentProps, PeriscopeBodyComponent } from '@periscopes/types';
import { PeriscopeMenuTabs, PeriscopeTabArea } from '@periscopes/general';
import { PeriscopeSfFieldSummary, SonarObjectType } from '@sonar-software/sonar-ts-types';

type FieldScopeProps = PeriscopeBaseComponentProps<PeriscopeSfFieldSummary>;

export const FieldScope: PeriscopeBodyComponent<FieldScopeProps> = ({ data, index }) => {
	const { sonarObjectId, relatedCounts } = data;

	return (
		<>
			<FieldScopeHeader
				data={ data }
				index={ index } />
			<PeriscopeMenuTabs>
				<PeriscopeTabArea
					forceRender
					key={ PERISCOPE_TAB_TYPES.DETAILS }
					tab={ PERISCOPE_TAB_TYPES.DETAILS }>
					<FieldMetadataTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforcePicklistValue ] ?? 0 }
					hidden={ !relatedCounts[ SonarObjectType.SalesforcePicklistValue ] }
					key={ PERISCOPE_TAB_TYPES.PICKLIST_VALUES }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.PICKLIST_VALUES }>
					<FieldPicklistValuesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceValidationRule ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.VALIDATION_RULES }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.VALIDATION_RULES }>
					<FieldValidationRulesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceWorkflowRule ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.WORKFLOW_RULES }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.WORKFLOW_RULES }>
					<FieldWorkflowRulesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceFieldUpdate ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.FIELD_UPDATES }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.FIELD_UPDATES }>
					<FieldFieldUpdatesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceEmailTemplate ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.EMAIL_TEMPLATES }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.EMAIL_TEMPLATES }>
					<FieldEmailTemplatesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceFlow ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.FLOWS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.FLOWS }>
					<FieldFlowsTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceProcessBuilder ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.PROCESS_BUILDERS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.PROCESS_BUILDERS }>
					<FieldProcessBuildersTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceApexClass ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.APEX_CLASSES }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.APEX_CLASSES }>
					<FieldApexClassesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceApexTrigger ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.APEX_TRIGGERS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.APEX_TRIGGERS }>
					<FieldApexTriggersTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceWorkflowAlert ] ?? 0 }
					group={ PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION }
					key={ PERISCOPE_TAB_TYPES.EMAIL_ALERTS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.EMAIL_ALERTS }>
					<FieldEmailAlertsTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts.sfdcFormulaField ?? 0 }
					key={ PERISCOPE_TAB_TYPES.FORMULA_FIELDS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.FORMULA_FIELDS }>
					<FieldFormulaFieldsTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforcePageLayout ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.PAGE_LAYOUTS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.PAGE_LAYOUTS }>
					<FieldPageLayoutsTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceProfile ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.PROFILES }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.PROFILES }>
					<FieldProfilesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceReport ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.REPORTS }
					requiresCount
					submenu={ PERISCOPE_TAB_TYPES.REFERENCES }
					tab={ PERISCOPE_TAB_TYPES.REPORTS }>
					<FieldReportsTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					key={ PERISCOPE_TAB_TYPES.ACTIVITY }
					tab={ PERISCOPE_TAB_TYPES.ACTIVITY }>
					<FieldTimelineTab sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.PardotField ] ?? 0 }
					featureFlag={ FeatureFlag.PardotEnabled }
					key={ PERISCOPE_TAB_TYPES.RELATED_TECH }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.RELATED_TECH }>
					<FieldRelatedTechTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
			</PeriscopeMenuTabs>
		</>
	);
};
